<script setup>
import Commons from '@/shared/utils/commons'
import TableList from '../others/TableList.vue'
import { ref, computed } from 'vue'
import SlideDetailView from '../others/SlideDetailView.vue'
import RoleDetails from '../permissions/RoleDetails.vue'
import RoleEditor from '../permissions/RoleEditor.vue'
import TeammateDetails from '../permissions/TeammateDetails.vue'
import TeammateEditor from '../permissions/TeammateEditor.vue'
import { cloneDeep } from 'lodash-es'
import ActionTrigger from '@/features/data/presentation/components/ActionTrigger.vue'
import { EVENTS } from '@/shared/constants/commons'
import Events from '@/events/events'
import DataFetcher from '@/features/data/presentation/components/DataFetcher.vue'
import DataService from '@/features/data/dataService.js'
import { buildApiUrl } from '@/project/configs/api.js'
import AuthService from '@/features/auth/authService'
import PermissionsManager from '@/shared/utils/permissionsManager'

const canEditTeammate = computed(() => {
  const user = AuthService.getUserData(true)?.value
  const isSuperAdmin = user?.role === 'super-admin' || user?.role === 'admin' || user?.isSuperAdmin === true
  return isSuperAdmin || PermissionsManager.has('team-members:edit').value
})

// === Colonnes pour les ROLES ===
const rolesCols = [
  {
    name: 'Nom',
    id: 'name',
    value(el) {
      return [el.name || '-']
    },
  },
  {
    name: 'Permissions',
    id: 'permissions',
    value(el) {
      if (!Array.isArray(el.permissions) || el.permissions.length === 0)
        return '-'
      return el.permissions.join(', ')
    },
  },
  {
    name: 'Dernière modification',
    id: 'updatedAt',
    value(el) {
      return el.updatedAt ? Commons.time(el.updatedAt, 'DD MMM YYYY') : '-'
    },
  },
  {
    name: '',
    id: 'action',
    icon: () => '/images/icons/eye.png',
    onClick(el) {
      roleDetailData.value = cloneDeep(el)
    },
    value() {},
  },
]

// === Colonnes pour les COÉQUIPIERS ===
const teammatesCols = [
  {
    name: 'Equipier',
    id: 'name',
    value(el) {
      return [el.name || '-', el.email || '-']
    },
  },
  {
    name: 'Date d’ajout',
    id: 'createdAt',
    value(el) {
      return el.createdAt ? Commons.time(el.createdAt, 'DD MMM YYYY') : '-'
    },
  },
  {
    name: 'Dernière connexion',
    id: 'lastLogin',
    value(el) {
      return el.lastLogin ? Commons.time(el.lastLogin, 'DD MMM YYYY') : '-'
    },
  },
  {
    name: 'Roles',
    id: 'roles',
    value(el) {
      if (!Array.isArray(el.roles) || el.roles.length === 0) return '-'
      return el.roles.map(r => r.name).join(', ')
    },
  },
  {
    name: '',
    id: 'action',
    icon: () => '/images/icons/eye.png',
    onClick(el) {
      teammateDetailData.value = cloneDeep(el)
    },
    value() {},
  },
]

const roleButtons = [
  {
    title: '&plus; Ajouter',
    onClick() {
      roleDetailData.value = {}
      editRole.value = true
    },
  },
]

const teammateButtons = [
  {
    title: '&plus; Ajouter',
    onClick() {
      teammateDetailData.value = {}
      editTeammate.value = true
    },
  },
]

const roleDetailData = ref(null)
const editRole = ref(false)
const saveRole = ref(false)

const teammateDetailData = ref(null)
const editTeammate = ref(false)
const saveTeammate = ref(false)

async function deleteRole(data) {
  const res = await window.customPrompt({
    title: `Voulez-vous vraiment supprimer le rôle "${data.name}" ?`,
    formFields: [],
    successText: 'Supprimer',
    cancelText: 'Retour',
  })
  if (!res.accepted) return
  
  const roleRes = await DataService.triggerAction('deleteRole', { id: data.id, name: data.name })
  if (roleRes && roleRes.statusCode === 200) {
    roleDetailData.value = null
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Succès',
      body: 'Rôle supprimé avec succès.',
      type: 'success',
    })
  }
}

async function deleteTeammate(data) {
  const res = await window.customPrompt({
    title: `Voulez-vous vraiment supprimer le coéquipier "${data.name}" ?`,
    formFields: [],
    successText: 'Supprimer',
    cancelText: 'Retour',
  })
  if (!res.accepted) return

  try {
    const searchUrl = buildApiUrl('/user/rapidoo-admin/all', { queryParams: { email: data.email, group: 'ultraxeno' } })
    const searchRes = await fetch(searchUrl).then(r => r.json())
    
    if (searchRes.data && searchRes.data.length > 0) {
      const userId = searchRes.data[0].id
      await DataService.triggerAction('deleteAdminUser', { id: userId })
    }

    const teammateRes = await DataService.triggerAction('deleteTeammate', { id: data.id })
    if (teammateRes && teammateRes.statusCode === 200) {
      teammateDetailData.value = null
      Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
        title: 'Succès',
        body: 'Coéquipier supprimé avec succès.',
        type: 'success',
      })
    }
  } catch (err) {
    console.error(err)
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Erreur',
      body: 'Erreur lors de la suppression.',
      type: 'error',
    })
  }
}

const showPasswordModal = ref(false)
const targetTeammate = ref(null)
const newTeammatePassword = ref('')
const showModalPasswordText = ref(false)
const isUpdatingTeammatePassword = ref(false)

function openPasswordModal(data) {
  targetTeammate.value = data
  newTeammatePassword.value = ''
  showModalPasswordText.value = false
  showPasswordModal.value = true
}

async function submitPasswordChange() {
  if (!targetTeammate.value?.email) return

  const password = newTeammatePassword.value?.trim()
  if (!password || password.length < 6) {
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Erreur',
      body: 'Le mot de passe doit comporter au moins 6 caractères.',
      type: 'error',
    })
    return
  }

  isUpdatingTeammatePassword.value = true
  try {
    const data = targetTeammate.value
    const nameParts = (data.name || '').trim().split(' ')
    const firstName = nameParts.shift() || ''
    const lastName = nameParts.join(' ') || firstName

    const updateRes = await DataService.triggerAction('updateAdminUser', {
      email: data.email,
      firstName,
      lastName,
      password: password,
    })

    if (updateRes && updateRes.statusCode === 200) {
      showPasswordModal.value = false
      newTeammatePassword.value = ''
      Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
        title: 'Succès',
        body: 'Mot de passe mis à jour avec succès.',
        type: 'success',
      })
    } else {
      Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
        title: 'Erreur',
        body: updateRes?.message || 'Erreur lors de la mise à jour du mot de passe.',
        type: 'error',
      })
    }
  } catch (err) {
    console.error(err)
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Erreur',
      body: 'Une erreur est survenue lors de la mise à jour.',
      type: 'error',
    })
  } finally {
    isUpdatingTeammatePassword.value = false
  }
}
</script>

<template>
  <div class="p-5 h-full overflow-auto">
    <!-- Table des Roles -->
    <TableList
      title="Roles"
      dataKey="roles"
      hasSearch
      :cols="rolesCols"
      :buttons="roleButtons"
    />

    <div class="py-5"></div>

    <!-- Table des Coéquipiers -->
    <TableList
      title="Coéquipiers"
      dataKey="teammates"
      hasSearch
      :cols="teammatesCols"
      :buttons="teammateButtons"
    />
  </div>

  <!-- Détails et édition Role -->
  <SlideDetailView
    :isVisible="!!roleDetailData?.id"
    @hide="roleDetailData = null"
  >
    <div class="flex gap-4 absolute right-14 top-4">
      <button
        class="buttonComponent small border rounded-xl bg-red-500"
        @click="deleteRole(roleDetailData)"
      >
        Supprimer
      </button>
      <button
        class="buttonComponent small border rounded-xl"
        @click="editRole = true"
      >
        Modifier
      </button>
    </div>
    <RoleDetails v-if="roleDetailData?.id" :roleData="roleDetailData" />
  </SlideDetailView>

  <SlideDetailView
    :isVisible="!!roleDetailData && editRole"
    @hide="editRole = false"
  >
    <RoleEditor
      v-if="roleDetailData"
      :roleData="roleDetailData"
      @saved="editRole = false"
    />
  </SlideDetailView>

  <!-- Détails et édition Coéquipier -->
  <SlideDetailView
    :isVisible="!!teammateDetailData?.id"
    @hide="teammateDetailData = null"
  >
    <div class="flex gap-4 absolute right-14 top-4">
      <button
        class="buttonComponent small border rounded-xl bg-red-500"
        @click="deleteTeammate(teammateDetailData)"
      >
        Supprimer
      </button>
      <button
        v-if="canEditTeammate"
        class="buttonComponent small border rounded-xl bg-amber-500 text-white"
        @click="openPasswordModal(teammateDetailData)"
      >
        Mot de passe
      </button>
      <button
        v-if="canEditTeammate"
        class="buttonComponent small border rounded-xl"
        @click="editTeammate = true"
      >
        Modifier
      </button>
    </div>
    <TeammateDetails
      v-if="teammateDetailData?.id"
      :teammateData="teammateDetailData"
    />
  </SlideDetailView>

  <SlideDetailView
    :isVisible="!!teammateDetailData && editTeammate"
    @hide="editTeammate = false"
  >
    <DataFetcher dataKey="roles" v-slot="{ data }">
      <TeammateEditor
        v-if="teammateDetailData"
        :teammateData="teammateDetailData"
        :rolesData="data?.result || []"
        @saved="editTeammate = false"
      />
    </DataFetcher>
  </SlideDetailView>

  <!-- Modal Dédié Modification Mot de Passe Coéquipier -->
  <div
    v-if="showPasswordModal"
    class="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4"
    @click.self="showPasswordModal = false"
  >
    <div class="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl space-y-4">
      <div class="flex items-center justify-between border-b pb-3">
        <h3 class="text-base font-bold text-gray-800 flex items-center gap-2">
          <i class="pi pi-key text-amber-500"></i>
          Modifier le mot de passe
        </h3>
        <button
          type="button"
          @click="showPasswordModal = false"
          class="text-gray-400 hover:text-gray-600 p-1"
        >
          <i class="pi pi-times"></i>
        </button>
      </div>

      <p class="text-sm text-gray-600">
        Définir un nouveau mot de passe pour
        <strong class="text-gray-800">{{ targetTeammate?.name || targetTeammate?.email }}</strong> :
      </p>

      <form @submit.prevent="submitPasswordChange" class="space-y-4">
        <div>
          <label class="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
            Nouveau mot de passe
          </label>
          <div class="relative">
            <input
              required
              :type="showModalPasswordText ? 'text' : 'password'"
              v-model="newTeammatePassword"
              minlength="6"
              placeholder="Au moins 6 caractères"
              class="w-full rounded-lg border border-gray-300 px-4 py-2.5 pr-10 text-sm text-gray-800 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
            />
            <button
              type="button"
              @click="showModalPasswordText = !showModalPasswordText"
              class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              <i :class="showModalPasswordText ? 'pi pi-eye-slash' : 'pi pi-eye'"></i>
            </button>
          </div>
        </div>

        <div class="flex gap-3 justify-end pt-2">
          <button
            type="button"
            @click="showPasswordModal = false"
            class="buttonComponent small border rounded-xl"
            :disabled="isUpdatingTeammatePassword"
          >
            Annuler
          </button>
          <button
            type="submit"
            class="buttonComponent small bg-amber-600 hover:bg-amber-700 text-white rounded-xl"
            :disabled="isUpdatingTeammatePassword"
          >
            {{ isUpdatingTeammatePassword ? 'Enregistrement...' : 'Enregistrer' }}
          </button>
        </div>
      </form>
    </div>
  </div>

</template>
